import * as React from 'react'
import Image from 'next/image'
import Link from 'next/link'

import { styled } from '@mui/material/styles'
import CardActionArea from '@mui/material/CardActionArea'
import AvatarGroup from '@mui/material/AvatarGroup'
import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { IconName } from 'components/atomic/atoms/icon/components/custom-icons'
import Text from 'components/atomic/atoms/text'
import Icon from 'components/atomic/atoms/icon'
import Chip from 'components/atomic/atoms/chip'

import { colorsFromItemType } from 'utils/paths'
import { PathOverview } from 'types/types'
import Routes from 'utils/routes'

const StyledCard = styled(Card)`
    height: 100%;
    box-shadow: none;
`

const CardContent = styled('div')`
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    gap: 1rem;
    justify-content: space-between;
    z-index: 1;
    top: 0;
    left: 0;
    padding: 1rem;
`

const BulletPoints = styled(Stack)`
    color: ${({ theme }) => theme.palette.text.secondary};
`

type PathCardProps = PathOverview

const PathCard = React.memo((props: PathCardProps) => {
    const { t }: I18n = useTranslation()

    const bulletPoints = React.useMemo((): { value: string; icon: IconName }[] => {
        return [
            {
                value: props.completion_time,
                icon: 'time',
            },
            {
                value: t(`common:difficulty.${props.difficulty}`),
                icon: props.difficulty.toLowerCase() as IconName,
            },
            {
                value: `${props.resources_count.total} Lezioni`,
                icon: 'handyman',
            },
        ]
    }, [t, props.completion_time, props.difficulty, props.resources_count])

    const fakeEnrolledStudentsNumber = React.useMemo(() => {
        return props._id
            .split('')
            .reduce(
                (acc, curr) => (isNaN(parseInt(curr)) ? acc : acc + parseInt(curr)),
                props?.short_description?.length * 3,
            )
    }, [props._id, props.short_description])

    const colors = React.useMemo(() => colorsFromItemType(props.goal), [props.goal])

    return (
        <Link href={Routes.Path(props.slug)}>
            <a>
                <StyledCard>
                    <CardActionArea sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" spacing={0}>
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={t(`common:pathGoal.${props.goal}`)}
                                        sx={{ borderColor: colors[0], backgroundColor: colors[1] }}
                                    />
                                    {!props.live ? (
                                        <Chip label={'Coming Soon'} size="small" />
                                    ) : props.pro_exclusive ? (
                                        <Chip label={'Pro'} golden size="small" variant="outlined" />
                                    ) : (
                                        <Chip label={'Gratis ✨'} size="small" variant="outlined" />
                                    )}
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing="0.2rem">
                                    <AvatarGroup spacing="medium">
                                        <Avatar
                                            alt=""
                                            sx={{ width: 20, height: 20 }}
                                            src={`https://i.pravatar.cc/20?u=${props._id}1`}
                                        />
                                        <Avatar
                                            alt=""
                                            sx={{ width: 20, height: 20 }}
                                            src={`https://i.pravatar.cc/20?u=${props._id}2`}
                                        />
                                        <Avatar
                                            alt=""
                                            sx={{ width: 20, height: 20 }}
                                            src={`https://i.pravatar.cc/20?u=${props._id}3`}
                                        />
                                    </AvatarGroup>
                                    <Text variant="caption" color="text.secondary">
                                        +{(fakeEnrolledStudentsNumber || 0) + (props.enrolled_users_count || 0)}{' '}
                                        Studenti
                                    </Text>
                                </Stack>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Text variant="h6" sx={{ maxWidth: '70%' }}>
                                    {props.title}
                                </Text>
                                {props.icon_url && (
                                    <Box sx={{ minWidth: 90 }}>
                                        <Image
                                            alt=""
                                            width={90}
                                            height={90}
                                            objectFit="contain"
                                            style={{ opacity: 0.2 }}
                                            src={props.icon_url}
                                        />
                                    </Box>
                                )}
                            </Stack>
                            <BulletPoints direction="row" alignItems="start">
                                {bulletPoints.map((item) => (
                                    <Stack
                                        sx={{ flex: 'none' }}
                                        direction="row"
                                        mr={2}
                                        spacing="0.2rem"
                                        key={item.icon}>
                                        <Text
                                            variant="caption"
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                            color="inherit">
                                            <Icon fontSize="inherit" sx={{ mr: 0.5 }} name={item.icon} />
                                            {item.value}
                                        </Text>
                                    </Stack>
                                ))}
                            </BulletPoints>
                        </CardContent>
                    </CardActionArea>
                </StyledCard>
            </a>
        </Link>
    )
})

export default PathCard
